// useEmploymentContracts.js
// Custom Hook for Employment Contracts Page State & Data Transformation Logic

import { useState, useEffect, useMemo, useCallback } from "react";
import {
  getEmploymentContracts,
  getContractStats,
  createEmploymentContract,
  updateEmploymentContract,
  renewEmploymentContract,
  deleteEmploymentContract,
  getFilterOptions,
  exportContractsData,
} from "../services/employmentContractService";

export const useEmploymentContracts = () => {
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    activePercentage: "0%",
    expiringSoon: 0,
    expired: 0,
  });
  const [filterOptions, setFilterOptions] = useState({
    statuses: ["All", "Active", "Expiring Soon", "Expired", "Pending"],
    types: ["All", "Permanent", "12 Month Contract", "6 Month Contract", "Internship", "Hourly", "Consultant", "Temporary"],
    departments: ["All"],
  });

  // Loading & Error States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters State
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [departmentFilter, setDepartmentFilter] = useState("All");
  const [advancedFilters, setAdvancedFilters] = useState({
    status: "All",
    type: "All",
    department: "All",
    startDate: "",
    endDate: "",
    expirationRange: "All",
    employmentType: "All",
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Drawer States
  const [selectedContract, setSelectedContract] = useState(null);
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false);
  const [isRenewDrawerOpen, setIsRenewDrawerOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [contractToRenew, setContractToRenew] = useState(null);

  // Toast Notification
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Load Initial Data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [contractsRes, optionsRes] = await Promise.all([
        getEmploymentContracts(),
        getFilterOptions(),
      ]);

      if (contractsRes.success) {
        setContracts(contractsRes.data);
        const computedStats = await getContractStats(contractsRes.data);
        setStats(computedStats);
      }

      if (optionsRes) {
        setFilterOptions((prev) => ({
          ...prev,
          ...optionsRes,
        }));
      }
    } catch (err) {
      console.error("Failed to load employment contracts data:", err);
      setError("Unable to load contracts. Please check network connection.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered Contracts Computation
  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      // Search query (name or ID)
      if (searchQuery.trim() !== "") {
        const query = searchQuery.toLowerCase().trim();
        const matchesName = contract.employee_name?.toLowerCase().includes(query);
        const matchesId = contract.employee_id?.toLowerCase().includes(query);
        const matchesType = contract.contract_type?.toLowerCase().includes(query);
        if (!matchesName && !matchesId && !matchesType) return false;
      }

      // Quick Filters
      if (statusFilter !== "All") {
        if (contract.status?.toLowerCase() !== statusFilter.toLowerCase()) return false;
      }

      if (typeFilter !== "All") {
        if (contract.contract_type?.toLowerCase() !== typeFilter.toLowerCase()) return false;
      }

      if (departmentFilter !== "All") {
        if (contract.department?.toLowerCase() !== departmentFilter.toLowerCase()) return false;
      }

      // Advanced Filters
      if (advancedFilters.status !== "All") {
        if (contract.status?.toLowerCase() !== advancedFilters.status.toLowerCase()) return false;
      }

      if (advancedFilters.type !== "All") {
        if (contract.contract_type?.toLowerCase() !== advancedFilters.type.toLowerCase()) return false;
      }

      if (advancedFilters.department !== "All") {
        if (contract.department?.toLowerCase() !== advancedFilters.department.toLowerCase()) return false;
      }

      if (advancedFilters.employmentType !== "All") {
        if (contract.employment_type?.toLowerCase() !== advancedFilters.employmentType.toLowerCase()) return false;
      }

      return true;
    });
  }, [contracts, searchQuery, statusFilter, typeFilter, departmentFilter, advancedFilters]);

  // Paginated Output
  const totalFilteredEntries = filteredContracts.length;
  const totalPages = Math.ceil(totalFilteredEntries / pageSize) || 1;
  const paginatedContracts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredContracts.slice(start, start + pageSize);
  }, [filteredContracts, currentPage, pageSize]);

  // Action Handlers
  const handleOpenDetails = (contract) => {
    setSelectedContract(contract);
    setIsDetailsDrawerOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsDrawerOpen(false);
    setSelectedContract(null);
  };

  const handleOpenCreateForm = () => {
    setEditingContract(null);
    setIsFormDrawerOpen(true);
  };

  const handleOpenEditForm = (contract) => {
    setEditingContract(contract);
    setIsFormDrawerOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormDrawerOpen(false);
    setEditingContract(null);
  };

  const handleOpenRenew = (contract) => {
    setContractToRenew(contract || selectedContract);
    setIsRenewDrawerOpen(true);
  };

  const handleCloseRenew = () => {
    setIsRenewDrawerOpen(false);
    setContractToRenew(null);
  };

  const handleOpenFilterDrawer = () => {
    setIsFilterDrawerOpen(true);
  };

  const handleCloseFilterDrawer = () => {
    setIsFilterDrawerOpen(false);
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setStatusFilter("All");
    setTypeFilter("All");
    setDepartmentFilter("All");
    setAdvancedFilters({
      status: "All",
      type: "All",
      department: "All",
      startDate: "",
      endDate: "",
      expirationRange: "All",
      employmentType: "All",
    });
    setCurrentPage(1);
    showToast("Filters reset successfully");
  };

  const handleSaveContract = async (payload) => {
    try {
      if (editingContract) {
        const res = await updateEmploymentContract(editingContract.uuid, payload);
        if (res.success) {
          setContracts((prev) => {
            const updated = prev.map((c) => (c.uuid === editingContract.uuid ? { ...c, ...res.data } : c));
            getContractStats(updated).then(setStats);
            return updated;
          });
          showToast("Contract updated successfully");
        }
      } else {
        const res = await createEmploymentContract(payload);
        if (res.success) {
          setContracts((prev) => {
            const updated = [res.data, ...prev];
            getContractStats(updated).then(setStats);
            return updated;
          });
          showToast("New contract created successfully");
        }
      }
      handleCloseForm();
    } catch (err) {
      showToast("Failed to save contract", "error");
    }
  };

  const handleRenewContractSubmit = async (renewalPayload) => {
    const targetUuid = contractToRenew?.uuid || selectedContract?.uuid;
    if (!targetUuid) return;

    try {
      const res = await renewEmploymentContract(targetUuid, renewalPayload);
      if (res.success) {
        setContracts((prev) => {
          const updated = prev.map((c) => (c.uuid === targetUuid ? { ...c, ...res.data } : c));
          getContractStats(updated).then(setStats);
          return updated;
        });
        if (selectedContract?.uuid === targetUuid) {
          setSelectedContract((prev) => ({
            ...prev,
            ...res.data,
          }));
        }
        showToast("Contract renewed successfully");
      }
      handleCloseRenew();
    } catch (err) {
      showToast("Failed to renew contract", "error");
    }
  };

  const handleDeleteContract = async (contract) => {
    if (!window.confirm(`Are you sure you want to delete contract ${contract.employee_id || contract.uuid}?`)) {
      return;
    }
    try {
      const res = await deleteEmploymentContract(contract.uuid);
      if (res.success) {
        setContracts((prev) => {
          const updated = prev.filter((c) => c.uuid !== contract.uuid);
          getContractStats(updated).then(setStats);
          return updated;
        });
        if (selectedContract?.uuid === contract.uuid) {
          handleCloseDetails();
        }
        showToast("Contract deleted successfully");
      }
    } catch (err) {
      showToast("Failed to delete contract", "error");
    }
  };

  const handleExport = async (format = "csv") => {
    await exportContractsData(format);
    showToast(`Exported contracts as ${format.toUpperCase()}`);
  };

  return {
    contracts: paginatedContracts,
    allContracts: contracts,
    totalEntries: contracts.length,
    filteredCount: totalFilteredEntries,
    stats,
    filterOptions,
    loading,
    error,
    toast,
    // Filters
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    departmentFilter,
    setDepartmentFilter,
    advancedFilters,
    setAdvancedFilters,
    handleResetFilters,
    // Pagination
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    pagination: {
      page: currentPage,
      pageSize: pageSize,
      totalItems: filteredContracts.length,
      totalPages: Math.max(1, Math.ceil(filteredContracts.length / pageSize)),
    },
    previousPage: () => setCurrentPage((p) => Math.max(1, p - 1)),
    nextPage: () => setCurrentPage((p) => Math.min(Math.max(1, Math.ceil(filteredContracts.length / pageSize)), p + 1)),
    // Drawers
    selectedContract,
    editingContract,
    contractToRenew,
    isDetailsDrawerOpen,
    isFormDrawerOpen,
    isRenewDrawerOpen,
    isFilterDrawerOpen,
    handleOpenDetails,
    handleCloseDetails,
    handleOpenCreateForm,
    handleOpenEditForm,
    handleCloseForm,
    handleOpenRenew,
    handleCloseRenew,
    handleOpenFilterDrawer,
    handleCloseFilterDrawer,
    // Actions
    handleSaveContract,
    handleRenewContractSubmit,
    handleDeleteContract,
    handleExport,
    refresh: loadData,
  };
};

export default useEmploymentContracts;
